import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAssigneesByProjectSeq } from "../../apis/user";
import { useTheme } from "@hooks/useTheme";
import { downloadBySource, formatDate, formatFullDateTime, sortByKey } from "@utils/common";
import ArrowRightIcon from "../Icons/ArrowRightIcon";
import ChevronUpIcon from "../Icons/ChevronUpIcon";
import DownloadIcon from "../Icons/DownloadIcon";
import FileTextIcon from "../Icons/FileTextIcon";
import TabsContainer from "../Tabs/TabsContainer";
import CustomSelect from "../UI/CustomSelect";
import { formatSeverityLabel, getSeverityBadgeClasses } from "../UI/SecurityInformation/severityStyles";
import AddCommentModal from "./AddCommentModal";
import AddNewRequestModal from "./AddNewRequestModal";
import ApproveRequestModal from "./ApproveRequestModal";
import CompleteRequestModal from "./CompleteRequestModal";
import RejectRequestModal from "./RejectRequestModal";
import { useLocation } from "react-router-dom";
import { useUserInfo } from "../../hooks/useUserInfo";
import { BUTTONS } from "@constants/index";
import { PermissionContext } from "../../contexts/PermissionContext";

const COMMUNICATION_FILTER_STEP_MAP = {
  "request": "Request",
  "analysis-check": "Analysis Check",
  "request-usage": "Request Usage Type Review",
  "usage": "Usage type review",
  "usage-input": "Usage type input",
  "verif": "Verif. Result",
  "fulfillment": "Fulfillment",
  "check-follow-up": "Follow-up check",
};

const Communication = ({
  requests = [],
  systemMessages = [],
  verificationData = null,
  verificationType = "binary", // "binary", "source_code", "sbom"
  verificationSeq = null,
  onApprove,
  onReject,
  onComplete,
  onAddRequest,
  onAddComment,
  isAddCommentPending = false,
  projectSeq = null,
  projectData = null,
  creatorList = [],
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const { canAddCommentOnStep, getRequestActionButtons } = useContext(PermissionContext);

  const [localRequests, setLocalRequests] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0 for Comments, 1 for Requests, 2 for System Messages
  const [activeFilter, setActiveFilter] = useState("all");
  const [isAddNewRequestModalOpen, setIsAddNewRequestModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedSystemMessage, setSelectedSystemMessage] = useState(null);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingCommentData, setExistingCommentData] = useState(null);
  const [expandedSecurityAlertSeqs, setExpandedSecurityAlertSeqs] = useState(() => new Set());

  const [formData, setFormData] = useState({
    assignee: "all",
  });

  const { userId: currentUserId } = useUserInfo();
  const existingComment = getExistingCommentData();
  // This function includes logic to check ownership permissions
  const canUserEdit = shouldShowEditButton();
  // Check if step is allowed using PermissionContext
  const isStepAllowed = verificationData 
    ? canAddCommentOnStep(verificationData.verificationStatusCd, verificationData.verificationStatusCdName)
    : false;
  // Condition to add: Step must be allowed AND no existing comment.
  const canAddComment = isStepAllowed && !existingComment;
  // Condition to edit: Step must be allowed AND user has permission to edit (existing comment + is owner).
  const canEditComment = isStepAllowed && canUserEdit;

  const { activeTabFromDashboard, activeTab: activeTabFromState } = location.state || {};

  const filterConfig = [
    { key: "all", labelKey: "communication.all", showOn: ["binary", "source_code", "clarityair"] },
    { key: "request", labelKey: "communication.request", showOn: ["binary", "source_code", "clarityair"] },
    { key: "analysis-check", labelKey: "communication.analysis-check", showOn: ["source_code", "clarityair"] },
    { key: "request-usage", labelKey: "communication.request-usage-type-review", showOn: ["binary"] },
    { key: "usage", labelKey: "communication.usage-type-review", showOn: ["binary", "source_code", "clarityair"] },
    { key: "usage-input", labelKey: "communication.usage-type-input", showOn: ["binary", "source_code", "clarityair"] },
    { key: "verif", labelKey: "common.review", showOn: ["binary", "source_code", "clarityair"] },
    { key: "fulfillment", labelKey: "status.standbyFollowup", showOn: ["binary", "source_code", "clarityair"] },
    { key: "check-follow-up", labelKey: "communication.check-follow-up", showOn: ["binary", "source_code", "clarityair"] },
  ]

  // Map activeTab string to index
  const getTabIndexFromString = (tabName) => {
    switch (tabName) {
      case "comments":
        return 0;
      case "requests":
        return 1;
      case "system-messages":
        return 2;
      default:
        return 0;
    }
  };

  // Set activeTab when component mounts if activeTabFromState is provided
  useEffect(() => {
    if (activeTabFromState) {
      const tabIndex = getTabIndexFromString(activeTabFromState);
      setActiveTab(tabIndex);
    }
  }, [activeTabFromState]);

  useEffect(() => {
    // Set isExpanded field for UI purpose
    setLocalRequests(requests.map((r) => {
      const isExpanded = localRequests.find((e) => e.id === r.id)?.isExpanded;
      return { ...r, isExpanded: isExpanded === undefined ? true : isExpanded }; // Expanded by default
    }))
  }, [requests])

  // Helper function to translate action status
  const translateActionStatus = (actionStatus) => {
    if (!actionStatus) return t("communication.unknown");

    const statusMap = {
      "PENDING COMPLETION": t("communication.pending-completion"),
      "PENDING APPROVAL": t("communication.pending-approval"),
      "DONE": t("communication.done"),
      "Completed": t("communication.done"),
      "Rejected": t("communication.rejected"),
      "Approved": t("communication.approved"),
      "UNKNOWN": t("communication.unknown"),
    };

    return statusMap[actionStatus] || actionStatus;
  };

  // Helper function to translate request status for step tags
  const translateRequestStatus = (status) => {
    if (!status) return "";

    const statusTranslationMap = {
      "Verif. Result": t("common.review"),
      "Fulfillment": t("status.standbyFollowup"),
      "Follow-up check": t("communication.check-follow-up"),
    };

    if (statusTranslationMap[status]) {
      return statusTranslationMap[status];
    }

    const normalizedStatus = status.toLowerCase().replace(/ /g, "-");
    const translationKey = `communication.step-tag-${normalizedStatus}`;
    const translated = t(translationKey, status);
    
    return translated === translationKey ? status : translated;
  };

  // New: options for assignee dropdown fetched from API (fallback to creatorList)
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  useEffect(() => {
    const allOption = { value: "all", label: t("communication.all") + " Members" };

    // If projectSeq is missing, fallback to creatorList
    if (!projectSeq) {
      setAssigneeOptions([allOption, ...creatorList]);
      return;
    }

    let isMounted = true;
    getAssigneesByProjectSeq(projectSeq)
      .then((data) => {
        if (!isMounted) return;
        const mapped = Array.isArray(data)
          ? data.map((u) => ({ value: u.userId, label: u.userName, roleCdName: u.roleCdName }))
          : [];

        // Add createrId to the options if available
        let finalOptions = [allOption, ...mapped];
        if (projectData?.createrId && !mapped.find((opt) => opt.value === projectData.createrId)) {
          finalOptions.push({
            value: projectData.createrId,
            label: projectData.createrId,
            roleCdName: "Creator",
          });
        }

        setAssigneeOptions(finalOptions);
      })
      .catch(() => {
        if (!isMounted) return;
        // Fallback gracefully
        let fallbackOptions = [allOption, ...creatorList];
        if (projectData?.createrId && !creatorList.find((opt) => opt.value === projectData.createrId)) {
          fallbackOptions.push({
            value: projectData.createrId,
            label: projectData.createrId,
            roleCdName: "Creator",
          });
        }
        setAssigneeOptions(fallbackOptions);
      });

    return () => {
      isMounted = false;
    };
  }, [projectSeq, t]);

  // Transform verificationData to comments format
  const transformVerificationDataToComments = (verificationData) => {
    if (!verificationData) return [];

    const comments = [];

    // Helper function to find user info from creatorList
    const findUserInfo = (userId) => {
      const userInfo = creatorList.find((user) => user.value === userId);
      return {
        name: userInfo?.label || userId || t("communication.unknown"),
        role: userInfo?.roleCdName || t("communication.unknown"),
      };
    };

    // Request comment
    if (verificationData.requestComment) {
      const requesterInfo = findUserInfo(verificationData.requesterId);
      comments.push({
        id: "request-comment",
        date: verificationData.requestedAt,
        status: "Request",
        requester: requesterInfo,
        description: verificationData.requestComment || t("communication.no-request-comment"),
        attachment: null, // Request comment doesn't have attachment
      });
    }

    // Usage Type confirm comment
    if (verificationData.approvalComment || verificationData.approvalCommentId) {
      const approverInfo = findUserInfo(verificationData.approvalCommentId);
      comments.push({
        id: "usage-type-confirm-comment",
        date: verificationData.approvalCommentAt,
        status: "Usage type review",
        requester: approverInfo,
        description: verificationData.approvalComment || t("communication.no-approval-comment"),
        attachment: null, // Usage type confirm doesn't have attachment
      });
    }

    // Verif. Result comment
    if (verificationData.verificationComment || verificationData.verificationCommentId) {
      const verifierInfo = findUserInfo(verificationData.verificationCommentId);
      comments.push({
        id: "verif-result-comment",
        date: verificationData.verificationCommentAt,
        status: "Verif. Result",
        requester: verifierInfo,
        description: verificationData.verificationComment || t("communication.no-verification-comment"),
        attachment: {
          fileName: verificationData.verificationRealFileName,
          serverFileName: verificationData.verificationServerFileName,
          middlePath: verificationData.verificationMiddlePath,
        },
      });
    }

    // Fulfillment comment
    if (verificationData.followupComment || verificationData.fulfillmentCommentId) {
      const fulfillerInfo = findUserInfo(verificationData.fulfillmentCommentId);
      comments.push({
        id: "fulfillment-comment",
        date: verificationData.fulfillmentCommentAt,
        status: "Fulfillment",
        requester: fulfillerInfo,
        description: verificationData.followupComment || t("communication.no-fulfillment-comment"),
        attachment: {
          fileName: verificationData.followupRealFileName,
          serverFileName: verificationData.followupServerFileName,
          middlePath: verificationData.followupMiddlePath,
        },
      });
    }

    // Usage Type review comment
    if (verificationData.usageTypeReviewComment || verificationData.usageTypeReviewCommentId) {
      const reviewerInfo = findUserInfo(verificationData.usageTypeReviewCommentId);
      comments.push({
        id: "usage-type-review-comment",
        date: verificationData.usageTypeReviewCommentAt,
        status: "Request Usage Type Review",
        requester: reviewerInfo,
        description: verificationData.usageTypeReviewComment || t("communication.no-usage-type-review-comment"),
        attachment: null, // Usage type review doesn't have attachment
      });
    }

    // Usage Type input comment
    if (verificationData.usageTypeInputComment || verificationData.usageTypeInputCommentId) {
      const inputterInfo = findUserInfo(verificationData.usageTypeInputCommentId);
      comments.push({
        id: "usage-type-input-comment",
        date: verificationData.usageTypeInputCommentAt,
        status: "Usage type input",
        requester: inputterInfo,
        description: verificationData.usageTypeInputComment || t("communication.no-usage-type-input-comment"),
        attachment: null, // Usage type input doesn't have attachment
      });
    }

    // Follow-up check comment
    if (verificationData.checkFollowUpComment || verificationData.checkFollowUpCommentId) {
      const checkerInfo = findUserInfo(verificationData.checkFollowUpCommentId);
      comments.push({
        id: "follow-up-check-comment",
        date: verificationData.checkFollowUpCommentAt,
        status: "Follow-up check",
        requester: checkerInfo,
        description: verificationData.checkFollowUpComment || t("communication.no-follow-up-check-comment"),
        attachment: null, // Follow-up check doesn't have attachment
      });
    }

    // Analysis check comment
    if (verificationData.analysisCheckComment || verificationData.analysisCheckCommentId) {
      const checkerInfo = findUserInfo(verificationData.analysisCheckCommentId);
      comments.push({
        id: "analysis-check-comment",
        date: verificationData.analysisCheckCommentAt,
        status: "Analysis Check",
        requester: checkerInfo,
        description: verificationData.analysisCheckComment || t("communication.no-analysis-check-comment"),
        attachment: null, // Analysis check doesn't have attachment
      });
    }

    return sortByKey(comments, 'date', 'desc', 'date');
  };

  const toggleExpanded = (request) => {
    setLocalRequests(localRequests.map((r) => ({ ...r, isExpanded: r.id === request.id ? !r.isExpanded : r.isExpanded })))
  }

  const handleAddRequest = (requestData) => {
    if (onAddRequest) {
      onAddRequest(requestData);
    }
  };

  const handleAddNewRequest = (requestData) => {
    if (onAddRequest) {
      onAddRequest(requestData);
    }
  };

  const handleCreateRequestFromSystemMessage = (message) => {
    setSelectedSystemMessage(message);
    setIsAddNewRequestModalOpen(true);
  };

  const handleApproveClick = (requestId) => {
    setSelectedRequestId(requestId);
    setIsApproveModalOpen(true);
  };

  const handleRejectClick = (requestId) => {
    setSelectedRequestId(requestId);
    setIsRejectModalOpen(true);
  };

  const handleCompleteClick = (requestId) => {
    setSelectedRequestId(requestId);
    setIsCompleteModalOpen(true);
  };

  const handleApproveSubmit = (approvalData) => {
    if (onApprove) {
      onApprove(approvalData);
    }
  };

  const handleRejectSubmit = (rejectionData) => {
    if (onReject) {
      onReject(rejectionData);
    }
  };

  const handleCompleteSubmit = (completeData) => {
    if (onComplete) {
      onComplete(completeData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Get existing comment data based on verification status
  function getExistingCommentData() {
    if (!verificationData) return null;

    const { verificationStatusCd, verificationStatusCdName } = verificationData;

    // Check for existing comment based on status
    if (verificationStatusCd === "ON_REVIEW") {
      if (verificationData.verificationComment) {
        return {
          commentMessage: verificationData.verificationComment,
          attachment: verificationData.verificationRealFileName
            ? {
              fileName: verificationData.verificationRealFileName,
              serverFileName: verificationData.verificationServerFileName,
              middlePath: verificationData.verificationMiddlePath,
            }
            : null,
          creatorId: verificationData.verificationCommentId,
        };
      }
    } else if (verificationStatusCd === "STANDBY_FOLLOWUP") {
      if (verificationData.followupComment) {
        return {
          commentMessage: verificationData.followupComment,
          attachment: verificationData.followupRealFileName
            ? {
              fileName: verificationData.followupRealFileName,
              serverFileName: verificationData.followupServerFileName,
              middlePath: verificationData.followupMiddlePath,
            }
            : null,
          creatorId: verificationData.fulfillmentCommentId,
        };
      }
    } else if (verificationStatusCd === "ON_VERIFICATION" && verificationStatusCdName === "Usage Type Check") {
      if (verificationData.approvalComment) {
        return {
          commentMessage: verificationData.approvalComment,
          attachment: null, // Usage Type Check doesn't have file attachment
          creatorId: verificationData.approvalCommentId,
        };
      }
    } else if (verificationStatusCd === "ON_VERIFICATION" && verificationStatusCdName === "Usage Type Review") {
      if (verificationData.usageTypeReviewComment) {
        return {
          commentMessage: verificationData.usageTypeReviewComment,
          attachment: null, // Usage Type Review doesn't have file attachment
          creatorId: verificationData.usageTypeReviewCommentId,
        };
      }
    }
    else if (verificationStatusCd === "ON_VERIFICATION" && verificationStatusCdName === "Usage Type Input") {
      if (verificationData.usageTypeInputComment) {
        return {
          commentMessage: verificationData.usageTypeInputComment,
          attachment: null, // Usage Type Input doesn't have file attachment
          creatorId: verificationData.usageTypeInputCommentId,
        };
      }
    } else if (verificationStatusCd === "FOLLOWUP_CHECK") {
      if (verificationData.checkFollowUpComment) {
        return {
          commentMessage: verificationData.checkFollowUpComment,
          attachment: null, // Follow-up check doesn't have file attachment
          creatorId: verificationData.checkFollowUpCommentId,
        };
      }
    } else if (verificationStatusCd === "ANALYSIS_CHECK") {
      if (verificationData.analysisCheckComment) {
        return {
          commentMessage: verificationData.analysisCheckComment,
          attachment: null, // Analysis check doesn't have file attachment
          creatorId: verificationData.analysisCheckCommentId,
        };
      }
    }

    return null;
  };

  // Check if we should show Edit button instead of Add button
  function shouldShowEditButton() {
    const existingComment = getExistingCommentData();
    if (!existingComment) {
      return false;
    }
    const isOwner = existingComment.creatorId === currentUserId;
    return isOwner;
  };

  const handleOpenCommentModal = () => {
    const existingData = getExistingCommentData();
    if (existingData) {
      setIsEditMode(true);
      setExistingCommentData(existingData);
    } else {
      setIsEditMode(false);
      setExistingCommentData(null);
    }
    setIsAddCommentModalOpen(true);
  };

  const handleAddComment = (commentData) => {
    if (!verificationSeq) {
      console.error("verificationSeq is required");
      return;
    }

    // Helper function to get the correct verification sequence key based on type
    const getVerificationSeqKey = () => {
      switch (verificationType) {
        case "binary":
          return "binaryVerificationSeq";
        case "source_code":
          return "sourceCodeVerificationSeq";
        case "sbom":
          return "sbomVerificationSeq";
        case "clarityair":
          return "clarityAirVerificationSeq";
        default:
          return "binaryVerificationSeq";
      }
    };

    const verificationSeqKey = getVerificationSeqKey();
    const commentContent = commentData.commentMessage;
    let payload;

    // Branch 1: Handle cases WITH file upload (using FormData)
    if (
      verificationData?.verificationStatusCd === "ON_REVIEW" ||
      verificationData?.verificationStatusCd === "STANDBY_FOLLOWUP"
    ) {
      const formDataToSend = new FormData();
      formDataToSend.append(verificationSeqKey, verificationSeq);
      formDataToSend.append("comment", commentData.commentMessage);

      if (commentData.attachment) {
        // Check if it's a new file (File object) or existing file (object with fileName)
        if (commentData.attachment instanceof File) {
          // New file - append as usual
          formDataToSend.append("fileUpload", commentData.attachment);
        } else if (commentData.attachment.fileName) {
          // Existing file - create a File object from existing file data to simulate upload
          try {
            // Create a blob from the existing file data
            const blob = new Blob([], { type: "application/octet-stream" });
            const file = new File([blob], commentData.attachment.fileName);
            formDataToSend.append("fileUpload", file);
          } catch (error) {
            console.error("Error creating File object from existing file:", error);
          }
        }
      }
      payload = formDataToSend;
    }
    // Branch 2: Handle cases WITHOUT file upload (using JSON)
    else if (
      (verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
        (verificationData?.verificationStatusCdName === "Usage Type Check" ||
          verificationData?.verificationStatusCdName === "Usage Type Review" ||
          verificationData?.verificationStatusCdName === "Usage Type Input")) ||
      verificationData?.verificationStatusCd === "FOLLOWUP_CHECK" ||
      verificationData?.verificationStatusCd === "ANALYSIS_CHECK"
    ) {
      let specificCommentPayload = {};

      // Use switch to determine the correct parameter key
      switch (verificationData?.verificationStatusCdName) {
        case "Usage Type Check":
          specificCommentPayload.approvalComment = commentContent;
          break;
        case "Usage Type Review":
          specificCommentPayload.usageTypeReviewComment = commentContent;
          break;
        case "Usage Type Input":
          specificCommentPayload.usageTypeInputComment = commentContent;
          break;
        default:
          // Handle FOLLOWUP_CHECK and ANALYSIS_CHECK which have different verificationStatusCd
          if (verificationData?.verificationStatusCd === "FOLLOWUP_CHECK") {
            specificCommentPayload = { checkFollowUpComment: commentContent };
          } else if (verificationData?.verificationStatusCd === "ANALYSIS_CHECK") {
            specificCommentPayload = { analysisCheckComment: commentContent };
          }
          break;
      }

      payload = {
        [verificationSeqKey]: verificationSeq,
        ...specificCommentPayload,
      };
    } else {
      console.warn("Unhandled status in Communication.js handleAddComment:", verificationData?.verificationStatusCdName);
    }

    // Call the API mutation from the parent component if a payload was created
    if (payload && onAddComment) {
      onAddComment(payload);
    }

    // Close the modal and reset state
    setIsAddCommentModalOpen(false);
    setIsEditMode(false);
    setExistingCommentData(null);
  };

  const handleDownloadFile = (attachment, commentStatus) => {
    if (!attachment || !attachment.fileName || !verificationSeq) {
      console.error("Missing required data for download:", { attachment, commentStatus, verificationSeq });
      return;
    }

    // Get the correct endpoint based on verification type
    const getEndpoint = () => {
      switch (verificationType) {
        case "binary":
          return commentStatus === "Verif. Result" ? "binary_verification_file" : "binary_followup_file";
        case "source_code":
          return commentStatus === "Verif. Result" ? "source_code_verification_file" : "source_code_followup_file";
        case "sbom":
          return commentStatus === "Verif. Result" ? "sbom_verification_file" : "sbom_followup_file";
        case "clarityair":
          return commentStatus === "Verif. Result" ? "clarity_air_verification_file" : "clarity_air_followup_file";
        default:
          return commentStatus === "Verif. Result" ? "binary_verification_file" : "binary_followup_file";
      }
    };

    const endpoint = getEndpoint();
    downloadBySource(attachment.fileName, "downloadBySource", endpoint, verificationSeq);
  };

  // Helper function to determine which buttons to show
  const getButtonsToShow = (request) => {
    const { actionStatus, assignee, requester } = request;
    
    // Check if current user is assignee or requester
    const isAssignee = assignee?.assigneeId === currentUserId;
    const isRequester = requester?.requesterId === currentUserId;
    
    // Get buttons from PermissionContext
    const buttons = getRequestActionButtons(actionStatus, isAssignee, isRequester);
    
    // Map button strings to BUTTONS constants for consistency
    return buttons.map(button => {
      switch (button) {
        case "complete":
          return BUTTONS.COMPLETE;
        case "reject":
          return BUTTONS.REJECT;
        case "approve":
          return BUTTONS.APPROVE;
        default:
          return button;
      }
    });
  };

  const getStatusDotBackground = (status) => {
    switch (status) {
      case "Completed":
        return colors.bgStatusSuccess;
      case "Rejected":
        return colors.bgDanger;
      default:
        return colors.bgStatusMuted;
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case "Completed":
        return colors.textStatusCompleted;
      case "Rejected":
        return colors.textStatusRejected;
      default:
        return colors.textStatusDefault;
    }
  };

  const getStepTagStyles = (step) => {
    const styles = {
      Request: [colors.communicationTagRequest, colors.communicationFilterRequest],
      "Verif. Result": [colors.communicationTagVerification, colors.communicationFilterVerification],
      "Analysis Check": [colors.communicationTagAnalysis, colors.communicationFilterAnalysis],
      "Request Usage Type Review": [colors.communicationTagUsageSubtleBorder, colors.communicationFilterUsageSubtleBorder],
      "Usage type review": [colors.communicationTagUsage, colors.communicationFilterUsage],
      "Usage type input": [colors.communicationTagUsage, colors.communicationFilterUsage],
      Review: [colors.communicationTagReview],
      "Follow-up": [colors.communicationTagFollowUp],
      "Follow-up check": [colors.communicationTagFulfillment, colors.communicationFilterFulfillment],
      Fulfillment: [colors.communicationTagFulfillment, colors.communicationFilterFulfillment],
      Ready: [colors.communicationTagReady],
    };
    const [className, filterClassName] = styles[step] || [colors.communicationTagDefault];
    return { className, filterClassName };
  };

  const getFilterChipClassName = (filterKey, isActive) => {
    const step = COMMUNICATION_FILTER_STEP_MAP[filterKey] ?? null;
    const base = "text-sm rounded-3xl px-5 py-1 border transition-colors";

    if (!step) {
      return `${base} ${colors.borderPrimary} ${
        isActive
          ? `${colors.bgCommunicationFilterButtonActive} ${colors.textPrimary} font-semibold`
          : `${colors.bgCommunicationFilterButton} ${colors.textSubTitle} ${colors.hoverTextPrimary} font-medium`
      }`;
    }

    const stepStyles = getStepTagStyles(step);

    if (isActive) {
      const activeStyles = {
        Request: colors.filterChipRequestActive,
        "Analysis Check": colors.filterChipAnalysisActive,
        "Request Usage Type Review": colors.filterChipUsageActive,
        "Usage type review": colors.filterChipUsageActive,
        "Usage type input": colors.filterChipUsageActive,
        "Verif. Result": colors.filterChipVerificationActive,
        Fulfillment: colors.filterChipFulfillmentActive,
        "Follow-up check": colors.filterChipFulfillmentActive,
      };
      return `${base} ${activeStyles[step] ?? `${colors.bgCommunicationFilterButtonActive} ${colors.textPrimary} ${colors.borderPrimary}`} font-semibold`;
    }

    return `${base} ${colors.bgCommunicationFilterButton} ${stepStyles.filterClassName} font-medium hover:brightness-110`;
  };

  // Render Comment Card (simple view for Comments tab)
  const renderCommentCard = (request) => {
    const stepTagStyles = getStepTagStyles(request.status)

    return (
      <div
        key={request.id}
        data-testid={`communication-comment-${request.id}`}
        className={`rounded-lg border ${colors.borderPrimary} ${colors.bgCommunicationDetail} p-4`}
      >
        {/* Header Row - Avatar, Name, Role, Date, Status */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* User Avatar */}
            <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${colors.bgAvatarBlue}`}>
              <span className="text-sm font-normal">{request.requester.name.charAt(0)}</span>
            </div>

            {/* User Info */}
            <div className="min-w-0 items-center gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`inline-block truncate max-w-[240px] font-semibold text-base ${colors.textPrimary}`}
                  title={request.requester.name}
                >
                  {request.requester.name}
                </span>
                <span
                  className={`inline-block truncate max-w-[160px] font-medium text-[0.8125rem] ${colors.textSubTitle}`}
                  title={request.requester.role}
                >
                  ({request.requester.role.toLowerCase()})
                </span>
              </div>
              <span className={`${colors.textSubTitle} font-normal text-[0.8125rem]`}>{formatFullDateTime(request.date)}</span>
            </div>
          </div>

          {/* Step Tag */}
          <div className="flex items-center gap-2">
            {request.status && (
              <span
                className={`rounded-3xl px-3 py-1 text-sm font-medium border ${stepTagStyles.className}`}
              >
                {translateRequestStatus(request.status)}
              </span>
            )}
          </div>
        </div>

        {/* Content Row - Description */}
        <div className="">
          <p className={`${colors.textSubTitle} font-normal text-sm leading-relaxed break-words whitespace-pre-wrap`} style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            {request.description}
          </p>
          {/* File attachment */}
          {request.attachment && request.attachment.fileName && (
            <div className={`flex items-center gap-2 rounded-lg ${colors.bgFulfillmentStripe} p-3 w-fit mt-3`}>
              <FileTextIcon className={`h-4 w-4 ${colors.iconAttachment}`} />
              <span className={`text-sm ${colors.textMessage} font-medium`}>{request.attachment.fileName}</span>
              <button
                type="button"
                data-testid={`communication-comment-${request.id}-download-attachment-button`}
                onClick={() => handleDownloadFile(request.attachment, request.status)}
                // disabled={downloadFile.isPending}
                className={`flex items-center gap-1 ${colors.textActive} ml-2 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {/* {downloadFile.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                ) : ( */}
                <DownloadIcon className="h-4 w-4" />
                {/* )} */}
                {/* <span className={`text-sm font-semibold`}>
                  {downloadFile.isPending ? "Downloading..." : "Download"}
                </span> */}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  };

  // Render Request Card (full view for Requests tab)
  const renderRequestCard = (request) => (
    <div
      key={request.id}
      data-testid={`communication-request-${request.id}`}
      className={`rounded-lg border ${colors.borderPrimary} ${colors.bgCommunicationDetail}`}
    >
      {/* Request Header - Always visible */}
      <div
        className="mb-2 flex items-start justify-between pt-4 px-4 cursor-pointer"
        data-testid={`communication-request-${request.id}-header-toggle`}
        onClick={() => toggleExpanded(request)}
      >
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-4">
            {/* ChevronUpIcon for expand/collapse - only for Requests tab */}
            <button
              type="button"
              data-testid={`communication-request-${request.id}-expand-button`}
              onClick={() => toggleExpanded(request)}
              className={`flex shrink-0 items-center transition-transform duration-200 ${colors.textSecondary} hover:${colors.textPrimary}`}
            >
              <ChevronUpIcon
                className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${request.isExpanded ? "" : "rotate-180"}`}
              />
            </button>
            <span className={`${colors.textSubTitle} text-sm`}>{formatFullDateTime(request.date)}</span>
          </div>

          {/* Requester and Assignee - Always visible */}
          <div className="flex min-w-0 flex-wrap items-center gap-4">
            <div className="flex min-w-0 max-w-full items-center gap-2">
              <span className={`shrink-0 ${colors.textSubTitle} text-sm`}>{t("communication.requester")}</span>
              <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${colors.bgAvatarBlue}`}>
                <span className={`${colors.textPrimary} text-sm font-bold`}>
                  {request.requester.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <span
                className={`inline-block truncate max-w-[240px] ${colors.textPrimary} text-base font-semibold`}
                title={request.requester.name}
              >
                {request.requester.name}
              </span>
              <span
                className={`inline-block truncate max-w-[160px] ${colors.textSubTitle} text-sm font-normal`}
                title={request.requester.role}
              >
                ({request.requester.role.toLowerCase()})
              </span>
            </div>

            <ArrowRightIcon className={`h-4 w-4 shrink-0 ${colors.textSubTitle}`} />

            <div className="flex min-w-0 max-w-full items-center gap-2">
              <span className={`shrink-0 ${colors.textSubTitle} text-sm`}>{t("communication.assignee")}</span>
              <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${colors.bgAvatarGreen}`}>
                <span className={`${colors.textPrimary} text-sm font-bold`}>
                  {request.assignee.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <span
                className={`inline-block truncate max-w-[240px] ${colors.textPrimary} text-base font-semibold`}
                title={request.assignee.name}
              >
                {request.assignee.name}
              </span>
              <span
                className={`inline-block truncate max-w-[160px] ${colors.textSubTitle} text-sm font-normal`}
                title={request.assignee.role}
              >
                ({request.assignee.role.toLowerCase()})
              </span>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span
            className={`rounded-3xl ${colors.bgSecondary} px-4 py-1.5 text-sm uppercase font-semibold ${colors.textSubTitle}`}
          >
            {translateActionStatus(request.actionStatus)}
          </span>
        </div>
      </div>

      {/* Initial Request Description - Always visible */}
      <div className="px-4">
        <div>
          {request.cve && (
            <div className={`text-sm font-bold ${colors.textPrimary} flex-shrink-0 mb-2 break-words`}>
              {request.cve}
            </div>
          )}
          <div
            className={`text-sm font-normal leading-relaxed ${colors.textPrimary} flex-1 break-words whitespace-pre-wrap`}
            style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
          >
            {request.description}
          </div>
        </div>
      </div>

      {/* Timeline - Only visible when expanded */}
      {request.isExpanded && request.conversation && request.conversation.length > 0 && (
        <div className="relative mb-6">
          {/* Vertical timeline line */}
          <div className={`absolute top-0 bottom-27 w-0.5 ${colors.bgTimeline}`} style={{ left: "1.5rem" }}></div>

          {request.conversation.map((message, index) => (
            <div key={index} className="relative mb-6 pl-16">
              {/* Curved connection line */}
              <div className="absolute top-4 left-5 h-12 w-12">
                <div className={`absolute -top-9 left-0 h-10 w-12 rotate-90 rounded-br-3xl border-r-2 border-b-2 ${colors.borderTimeline}`}></div>
              </div>

              <div className="mt-12 flex items-start gap-4">
                {/* Avatar */}
                <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${colors.bgAvatarBlue}`}>
                  <span className={`text-sm font-bold ${colors.textPrimary}`}>{message.user.name.charAt(0)}</span>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-base font-semibold ${colors.textMessage}`}>{message.user.name}</span>
                      <div className={`h-2 w-2 rounded-full ${getStatusDotBackground(message.status)}`}></div>
                      <span className={`text-[0.8125rem] font-semibold ${getStatusTextColor(message.status)}`} bg-none>
                        {t(`communication.${message.status.toLowerCase()}`)}
                      </span>
                    </div>
                    <span className={`${colors.textSubTitle} font-normal text-[0.8125rem]`}>{formatFullDateTime(message.timestamp)}</span>
                  </div>
                  {message.content && (
                    <p className={`text-sm font-normal ${colors.textMessage} break-words whitespace-pre-wrap`} style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                      {message.content}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={`h-px w-full ${colors.bgDivider} my-4`}></div>

      {/* Action Buttons - Always visible */}
      <div className="flex justify-between mx-4 mb-4">
        <div className="items-center">
          <div
            className={`rounded-lg ${colors.bgCompletionRequestDate} px-4 py-1.5 text-sm font-semibold ${colors.textCompletionRequestDate} transition-colors`}
          >
            {t("communication.completion-request-date")}:{" "}
            {request.completionRequestDate
              ? formatDate(request.completionRequestDate)
              : "N/A"}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getButtonsToShow(request).map((buttonType) => {
            switch (buttonType) {
              case "reject":
                return (
                  <button
                    key="reject"
                    type="button"
                    data-testid={`communication-request-${request.id}-reject-button`}
                    onClick={() => handleRejectClick(request.id)}
                    className={`text-sm rounded-lg px-4 py-1.5 font-semibold ${colors.textDanger} border ${colors.borderDangerOutLineButton} transition-colors`}
                  >
                    {t("communication.reject")}
                  </button>
                );
              case "approve":
                return (
                  <button
                    key="approve"
                    type="button"
                    data-testid={`communication-request-${request.id}-approve-button`}
                    onClick={() => handleApproveClick(request.id)}
                    className={`text-sm rounded-lg ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} px-4 py-1.5 font-semibold transition-colors`}
                  >
                    {t("communication.approve")}
                  </button>
                );
              case "complete":
                return (
                  <button
                    key="complete"
                    type="button"
                    data-testid={`communication-request-${request.id}-complete-button`}
                    onClick={() => handleCompleteClick(request.id)}
                    className={`text-sm rounded-lg ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} px-4 py-1.5 font-semibold transition-colors`}
                  >
                    {t("communication.complete")}
                  </button>
                );
              default:
                return null;
            }
          })}
        </div>
      </div>
    </div>
  );

  const parseSecurityAlertComponents = (message) => {
    const raw = message.securityAlertContent;
    if (!raw) return [];
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return Array.isArray(parsed?.components) ? parsed.components : [];
    } catch {
      return [];
    }
  };

  const isSecurityAlertMessage = (message) =>
    message?.source === "SECURITY_ALERT" || String(message?.isSecurityAlertFlag || "").toUpperCase() === "Y";

  const toggleSecurityAlertExpanded = (actionSeq) => {
    setExpandedSecurityAlertSeqs((prev) => {
      const next = new Set(prev);
      if (next.has(actionSeq)) next.delete(actionSeq);
      else next.add(actionSeq);
      return next;
    });
  };

  const renderSecurityAlertSystemMessageCard = (message) => {
    const components = parseSecurityAlertComponents(message);
    const expanded = expandedSecurityAlertSeqs.has(message.actionSeq);
    const count = components.length;

    return (
      <div
        key={`SECURITY_ALERT-${message.actionSeq}`}
        data-testid={`communication-system-message-${message.actionSeq}`}
        className={`rounded-lg border border-l-4 border-l-orange-500 ${colors.borderPrimary} ${colors.bgCommunicationDetail}`}
      >
        <button
          type="button"
          className="w-full flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-3 text-left"
          onClick={() => toggleSecurityAlertExpanded(message.actionSeq)}
          aria-expanded={expanded}
          data-testid={`communication-system-message-${message.actionSeq}-toggle`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold bg-orange-500/15 text-orange-400 border border-orange-500/40">
              {t("communication.security-alert", "Security Alert")}
            </span>
            <span className={`${colors.textPrimary} text-sm font-semibold`}>
              {t("communication.security-alert-components-count", "{{count}} component(s)", { count })}
            </span>
          </div>
          <div className={`flex items-center gap-2 text-sm ${colors.textSubTitle} shrink-0`}>
            <span className={`${colors.textPrimary} font-medium`}>{message.createrId}</span>
            <span className={`h-3.5 w-px ${colors.bgPolicyConflict}`} />
            <span>{formatFullDateTime(message.createdAt)}</span>
            <ChevronUpIcon className={`h-4 w-4 transition-transform ${expanded ? "" : "rotate-180"}`} />
          </div>
        </button>

        {expanded ? (
          <div className={`border-t ${colors.borderPrimary} px-5 py-3 space-y-2`}>
            {components.length === 0 ? (
              <p className={`text-sm ${colors.textSubTitle}`}>
                {t("communication.no-security-alert-components", "No component details")}
              </p>
            ) : (
              components.map((component, index) => {
                const name = component.componentName || "";
                const version = component.componentVersion || "";
                const title = version ? `${name} (${version})` : name;
                const alertStatus = String(component.alertStatus || "").toLowerCase();
                const isRemoved = alertStatus === "removed";
                const severity = component.maxSeverity;
                return (
                  <div
                    key={`${name}@${version}-${index}`}
                    className={`rounded-md border ${colors.borderPrimary} px-3 py-2`}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold border ${
                          isRemoved
                            ? "bg-slate-500/15 text-slate-300 border-slate-500/40"
                            : "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                        }`}
                      >
                        {isRemoved
                          ? t("communication.alert-status-removed", "Removed")
                          : t("communication.alert-status-added", "Added")}
                      </span>
                      {severity ? (
                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold border ${getSeverityBadgeClasses(severity)}`}>
                          {formatSeverityLabel(severity)}
                        </span>
                      ) : null}
                    </div>
                    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-sm ${colors.textSubTitle}`}>
                      <span>
                        <span className="font-medium">{t("communication.component", "Component")}:</span>{" "}
                        <span className={`${colors.textPrimary} font-semibold`}>{title}</span>
                      </span>
                      <span>
                        <span className="font-medium">{t("communication.max-cvss", "Max CVSS")}:</span>{" "}
                        <span className={`${colors.textPrimary} font-semibold`}>{component.maxCvssScore ?? "N/A"}</span>
                      </span>
                      <span>
                        <span className="font-medium">{t("communication.cve-count", "CVE Count")}:</span>{" "}
                        <span className={`${colors.textPrimary} font-semibold`}>{component.cveCount ?? 0}</span>
                      </span>
                      <span>
                        <span className="font-medium">{t("communication.recommended-version", "Recommended Version")}:</span>{" "}
                        <span className={`${colors.textPrimary} font-semibold`}>{component.recommendedVersion || "N/A"}</span>
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : null}
      </div>
    );
  };

  // Render System Message Card
  const renderSystemMessageCard = (message) => {
    if (isSecurityAlertMessage(message)) {
      return renderSecurityAlertSystemMessageCard(message);
    }

    return (
    <div
      key={`${message.source || "CVE"}-${message.actionSeq}`}
      data-testid={`communication-system-message-${message.actionSeq}`}
      className={`rounded-lg border ${colors.borderPrimary} ${colors.bgCommunicationDetail}`}
    >
      <div className="flex items-center justify-between mx-6 mt-6">
        <div className="flex items-center gap-3">
          <span className={`${colors.textSubTitle} text-sm`}>{t("communication.created-by")}: </span>
          <span className={`${colors.textPrimary} text-sm font-medium`}>{message.createrId}</span>
          <div className={`h-4 w-px ${colors.bgPolicyConflict}`}></div>
          <span className={`${colors.textSubTitle} text-sm`}>
            {formatFullDateTime(message.createdAt)}
          </span>
        </div>
        <button
          type="button"
          data-testid={`communication-system-message-${message.actionSeq}-create-request-button`}
          onClick={() => handleCreateRequestFromSystemMessage(message)}
          className={`text-sm rounded-lg px-5 py-2.5 transition-colors ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} font-medium`}
        >
          {t("communication.create-request")}
        </button>
      </div>

      <div className={`h-px w-full ${colors.bgDivider} my-3`}></div>

      <div className="px-6 mb-6">
        <p className={`${colors.textPrimary} text-lg font-bold break-words`}>
          {message.cve}
        </p>
        {message.actionsContent ? (
          <p className={`${colors.textSubTitle} text-sm mt-2 break-words`}>
            {message.actionsContent}
          </p>
        ) : null}
      </div>
    </div>
  );
  };

  // Tab configurations

  const projectInfoTabs =
    verificationType === "sbom"
      ? [
        // SBOM: Comments only (no Security Alert / Requests)
        {
          label: `${t("communication.comments")} (${transformVerificationDataToComments(verificationData).length})`,
          content: (
            <div className="relative">
              {/* Filter Bar for Comments - SBOM limited filters */}
              <div className="mb-6 flex justify-between items-center">
                <div className="flex gap-3">
                  <button
                    type="button"
                    data-testid="communication-filter-all-button"
                    onClick={() => setActiveFilter("all")}
                    className={getFilterChipClassName("all", activeFilter === "all")}
                  >
                    {t("communication.all")}
                  </button>
                  <button
                    type="button"
                    data-testid="communication-filter-request-button"
                    onClick={() => setActiveFilter("request")}
                    className={getFilterChipClassName("request", activeFilter === "request")}
                  >
                    {t("communication.request")}
                  </button>
                  <button
                    type="button"
                    data-testid="communication-filter-verif-button"
                    onClick={() => setActiveFilter("verif")}
                    className={getFilterChipClassName("verif", activeFilter === "verif")}
                  >
                    {t("common.review")}
                  </button>
                </div>
                {canEditComment && (
                    <button
                        type="button"
                        data-testid="communication-edit-comment-button"
                        onClick={handleOpenCommentModal}
                        className={`text-sm rounded-lg px-5 py-2.5 border transition-colors ${colors.textActive} font-medium`}
                    >
                        {t("communication.edit-comment")}
                    </button>
                )}

                {canAddComment && (
                    <button
                        type="button"
                        data-testid="communication-add-comment-button"
                        onClick={handleOpenCommentModal}
                        className={`text-sm rounded-lg px-5 py-2.5 border transition-colors ${colors.textActive} font-medium`}
                    >
                        {t("communication.add-new-comment")}
                    </button>
                )}
              </div>

              {/* Comments List */}
              <div className="space-y-4">
                {transformVerificationDataToComments(verificationData)
                  .filter((comment) => {
                    if (activeFilter === "all") return true;
                    if (activeFilter === "request") return comment.status === "Request";
                    if (activeFilter === "verif") return comment.status === "Verif. Result";
                    return true;
                  })
                  .map((comment) => renderCommentCard(comment))}
              </div>
            </div>
          ),
        }
      ]
      : [
        // Binary/Source Code: Full tabs (Comments, Requests, System Messages)
        {
          label: `${t("communication.comments")} (${transformVerificationDataToComments(verificationData).length})`,
          content: (
            <div className="relative">
              {/* Filter Bar for Comments */}
              <div className="mb-6 flex justify-between items-start">
                <div className="flex flex-wrap gap-3">
                  {/* Filter buttons */}
                  {filterConfig
                    .filter(button => button.showOn.includes(verificationType)) // Filter buttons based on verificationType
                    .map(button => (
                      <button
                        key={button.key}
                        type="button"
                        data-testid={`communication-filter-${button.key}-button`}
                        onClick={() => setActiveFilter(button.key)}
                        className={getFilterChipClassName(button.key, activeFilter === button.key)}
                      >
                        {t(button.labelKey, button.key.replace(/_/g, ' '))}
                      </button>
                    ))
                  }
                </div>
                {canEditComment && (
                  <div className="flex-shrink-0">
                  <button
                    type="button"
                    data-testid="communication-edit-comment-button"
                    onClick={handleOpenCommentModal}
                    className={`text-sm rounded-lg px-5 py-2.5 border transition-colors ${colors.textActive} font-medium`}
                  >
                      {t("communication.edit-comment")}
                    </button>
                  </div>
                )}

                {canAddComment && (
                  <div className="flex-shrink-0">
                  <button
                    type="button"
                    data-testid="communication-add-comment-button"
                    onClick={handleOpenCommentModal}
                    className={`text-sm rounded-lg px-5 py-2.5 border transition-colors ${colors.textActive} font-medium`}
                  >
                      {t("communication.add-new-comment")}
                    </button>
                  </div>
                )}
              </div>

              {/* Comments List */}
              <div className="space-y-4">
                {transformVerificationDataToComments(verificationData)
                  .filter((comment) => {
                    if (activeFilter === "all") return true;
                    if (activeFilter === "request") return comment.status === "Request";
                    if (activeFilter === "analysis-check") return comment.status === "Analysis Check";
                    if (activeFilter === "request-usage") return comment.status === "Request Usage Type Review";
                    if (activeFilter === "usage") return comment.status === "Usage type review";
                    if (activeFilter === "usage-input") return comment.status === "Usage type input";
                    if (activeFilter === "verif") return comment.status === "Verif. Result";
                    if (activeFilter === "fulfillment") return comment.status === "Fulfillment";
                    if (activeFilter === "check-follow-up") return comment.status === "Follow-up check";
                    return true;
                  })
                  .map((comment) => renderCommentCard(comment))}
              </div>
            </div>
          ),
        },
        {
          label: `${t("communication.requests")} (${requests.length})`,
          content: (
            <div className="relative">
              {/* Filter Bar for Requests */}
              <div className="mb-6 flex justify-between items-start">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-base font-medium ${colors.textSubTitle}`}>
                    {t("communication.assignee")}
                  </span>
                  <CustomSelect
                    options={assigneeOptions}
                    value={assigneeOptions.find((opt) => opt.value === formData.assignee)}
                    onChange={(opt) => handleInputChange("assignee", opt.value)}
                    placeholder={t("common.please-select")}
                    className="w-48 text-sm"
                    clearable={false}
                    displayFormatLabelWithID={true}
                  />
                  <span className={`h-4 w-px mx-3 ${colors.bgPolicyConflict}`}></span>
                  <button
                    type="button"
                    data-testid="communication-requests-filter-all-button"
                    onClick={() => setActiveFilter("all")}
                    className={`text-sm rounded-3xl px-5 py-1 border ${colors.borderPrimary} transition-colors ${activeFilter === "all"
                      ? `${colors.bgCommunicationFilterButtonActive} ${colors.textPrimary} font-semibold`
                      : `${colors.bgCommunicationFilterButton} ${colors.textSubTitle} ${colors.hoverTextPrimary} font-medium`
                      }`}
                  >
                    {t("communication.all")}
                  </button>

                  <button
                    type="button"
                    data-testid="communication-requests-filter-pending-completion-button"
                    onClick={() => setActiveFilter("pending_completion")}
                    className={`text-sm rounded-3xl px-5 py-1 border ${colors.borderPrimary} transition-colors ${activeFilter === "pending_completion"
                        ? `${colors.bgCommunicationFilterButtonActive} ${colors.textPrimary} font-semibold`
                        : `${colors.bgCommunicationFilterButton} ${colors.textSubTitle} ${colors.hoverTextPrimary} font-medium`
                      }`}
                  >
                    {t("communication.pending-completion")}
                  </button>
                  <button
                    type="button"
                    data-testid="communication-requests-filter-pending-approval-button"
                    onClick={() => setActiveFilter("pending_approval")}
                    className={`text-sm rounded-3xl px-5 py-1 border ${colors.borderPrimary} transition-colors ${activeFilter === "pending_approval"
                      ? `${colors.bgCommunicationFilterButtonActive} ${colors.textPrimary} font-semibold`
                      : `${colors.bgCommunicationFilterButton} ${colors.textSubTitle} ${colors.hoverTextPrimary} font-medium`
                      }`}
                  >
                    {t("communication.pending-approval")}
                  </button>
                  <button
                    type="button"
                    data-testid="communication-requests-filter-done-button"
                    onClick={() => setActiveFilter("done")}
                    className={`text-sm rounded-3xl px-5 py-1 border ${colors.borderPrimary} transition-colors ${activeFilter === "done"
                      ? `${colors.bgCommunicationFilterButtonActive} ${colors.textPrimary} font-semibold`
                      : `${colors.bgCommunicationFilterButton} ${colors.textSubTitle} ${colors.hoverTextPrimary} font-medium`
                      }`}
                  >
                    {t("communication.done")}
                  </button>
                </div>
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    data-testid="communication-add-new-request-button"
                    onClick={() => setIsAddNewRequestModalOpen(true)}
                    className={`text-sm rounded-lg px-5 py-2.5 border transition-colors ${colors.textActive} font-medium`}
                  >
                    {t("communication.add-new-request")}
                  </button>
                </div>
              </div>

              {/* Requests List */}
              <div className="space-y-4">
                {localRequests
                  .filter((request) => {
                    // Filter by assignee
                    if (formData.assignee !== "all" && request.assignee?.assigneeId !== formData.assignee) {
                      return false;
                    }

                    // Filter by status
                    if (activeFilter === "all") return true;
                    if (activeFilter === "pending_completion") return request.actionStatus === "PENDING COMPLETION";
                    if (activeFilter === "pending_approval") return request.actionStatus === "PENDING APPROVAL";
                    if (activeFilter === "done") return request.actionStatus === "DONE";

                    return true;
                  })
                  .map((request) => renderRequestCard(request))}
              </div>
            </div>
          ),
        },
        {
          label: `${t("communication.system-messages")} (${systemMessages.length})`,
          content: (
            <div className="relative">
              {/* System Messages List */}
              <div className="space-y-4">{systemMessages.map((message) => renderSystemMessageCard(message))}</div>
            </div>
          ),
        },
      ];

  return (
    <div data-testid="communication">
      {/* Header */}
      <div className="mb-6">
        <h2 className={`${colors.textPrimary} pb-4 text-xl font-semibold uppercase`}>{t("communication.communication")}</h2>

        <div className={`mb-4 rounded-lg ${colors.bgPrimary} ${colors.borderPrimary}`}>
          <TabsContainer tabs={projectInfoTabs} defaultActiveTab={activeTabFromDashboard || activeTab} onTabChange={setActiveTab} dataTestId="communication-tabs" />
        </div>
      </div>

      {/* Add New Request Modal */}
      <AddNewRequestModal
        isOpen={isAddNewRequestModalOpen}
        onClose={() => {
          setIsAddNewRequestModalOpen(false);
          setSelectedSystemMessage(null);
        }}
        onSubmit={handleAddNewRequest}
        assigneeOptions={
          assigneeOptions.length > 0
            ? assigneeOptions.filter((option) => option.value !== "all")
            : [{ value: "no-members", label: t("communication.no-members-in-project"), isDisabled: true }]
        }
        systemMessage={selectedSystemMessage}
      />

      {/* Approve Request Modal */}
      <ApproveRequestModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onSubmit={handleApproveSubmit}
        requestId={selectedRequestId}
      />

      {/* Reject Request Modal */}
      <RejectRequestModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onSubmit={handleRejectSubmit}
        requestId={selectedRequestId}
      />

      {/* Complete Request Modal */}
      <CompleteRequestModal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        onSubmit={handleCompleteSubmit}
        requestId={selectedRequestId}
      />

      {/* Add Comment Modal */}
      <AddCommentModal
        isOpen={isAddCommentModalOpen}
        onClose={() => {
          setIsAddCommentModalOpen(false);
          setIsEditMode(false);
          setExistingCommentData(null);
        }}
        onSubmit={handleAddComment}
        verificationData={verificationData}
        isPending={isAddCommentPending}
        isEditMode={isEditMode}
        existingCommentData={existingCommentData}
      />
    </div>
  );
};

export default Communication;
